import React, { useState, useMemo } from "react";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Building2,
  TrendingUp,
  CheckCircle2,
  Activity,
  Wallet,
  RefreshCw,
  Server,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import DetailStatCardSkeleton from "@/shared/components/skeletons/DetailStatCardSkeleton";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import AreaStatsChart from "@/shared/components/dashboard/TransactionChart";
import { getDateRange } from "@/shared/utils/dateFilters";
import * as resellerService from "@/shared/api/services/resellerService";
import * as transactionService from "@/shared/api/services/transactionService";
import * as merchantService from "@/shared/api/services/merchantService";

export default function ResellerDashboard() {
  const { t } = useLanguage();
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedTx, setSelectedTx] = useState(null);

  // Fetch Profile to get reseller name
  const { data: profile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: () => resellerService.getProfile(),
  });

  const resellerName = profile?.name;
  const dateRange = useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  const startISO = dateRange.startDate.toISOString();
  const endISO = dateRange.endDate.toISOString();

  // Fetch Reseller's merchants to scope transactions
  const { data: resellerMerchants = [], isLoading: merchantsLoading, refetch: refetchMerchants } = useQuery({
    queryKey: ["reseller-merchants"],
    queryFn: () => resellerService.getMyMerchants(),
  });

  const merchantIds = useMemo(() => resellerMerchants.map(m => m.id), [resellerMerchants]);

  // Reseller panel dashboard stats
  const { data: panelStats, isPending: isPanelStatsLoading, refetch: refetchPanelStats } = useQuery({
    queryKey: ["reseller-panel-dashboard-stats", merchantIds, startISO, endISO],
    queryFn: () => resellerService.getPanelDashboardStats(startISO, endISO, merchantIds),
    enabled: merchantIds.length > 0,
    placeholderData: (prev) => prev,
  });

  // Chart data
  const { data: chartData = [], refetch: refetchChart } = useQuery({
    queryKey: ["reseller-dashboard-chart", merchantIds, startISO, endISO],
    queryFn: () => merchantService.getTransactionsChart(startISO, endISO, "PKR", merchantIds),
    enabled: merchantIds.length > 0,
    placeholderData: (prev) => prev,
  });

  // Recent transactions (5 most recent)
  const {
    data: recentTransactionsResult,
    isPending: isRecentLoading,
    refetch: refetchRecentTransactions,
  } = useQuery({
    queryKey: ["reseller-recent-transactions", merchantIds],
    queryFn: () => transactionService.getPaginatedTransactions(1, 5, "", {}, merchantIds),
  });
  const recentTransactions = recentTransactionsResult?.data || [];


  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refetchMerchants(),
        refetchTransactions(),
        refetchChart(),
        refetchRecentTransactions(),
        refetchPanelStats(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const statsCards = [
    { title: t("totalVolume"), value: `${panelStats?.totalVolume ?? 0}`, icon: Wallet, color: "bg-violet-100 text-violet-600" },
    { title: t("deposits"), value: `${panelStats?.totalDeposits ?? 0}`, icon: ArrowDownToLine, color: "bg-blue-100 text-blue-600" },
    { title: t("withdrawals"), value: `${panelStats?.totalWithdrawals ?? 0}`, icon: ArrowUpFromLine, color: "bg-emerald-100 text-emerald-600" },
    { title: t("successRate"), value: `${panelStats?.successRate ?? 0}%`, icon: CheckCircle2, color: "bg-green-100 text-green-600" },
    { title: t("merchants"), value: panelStats?.activeMerchants ?? 0, icon: Building2, color: "bg-amber-100 text-amber-600" },
  ];

  const quickActions = [
    { label: t("merchants"), to: "ResellerMerchants", icon: Building2, iconColor: "text-violet-600" },
    { label: t("transactions"), to: "ResellerTransactions", icon: Activity, iconColor: "text-blue-600" },
    { label: t("routing"), to: "ResellerRouting", icon: TrendingUp, iconColor: "text-emerald-600" },
    { label: t("providers"), to: "ResellerProviders", icon: Server, iconColor: "text-amber-600" },
  ];

  return (
    <ResellerLayout currentPage="ResellerDashboard" resellerName={resellerName}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
            <p className="text-slate-500">{t("welcomeBack")}, {resellerName}</p>
          </div>
          <div className="flex items-center gap-3">
            <DateFilter
              value={dateFilter}
              onChange={setDateFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              className="bg-white"
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
          {isPanelStatsLoading
            ? statsCards.map((_, i) => <DetailStatCardSkeleton key={i} />)
            : statsCards.map((stat, i) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl p-4 lg:p-5 shadow-sm border border-slate-100"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-slate-500 mt-0.5">{stat.title}</p>
                    <p className="text-md lg:text-xl font-bold text-slate-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="h-5 w-5 lg:h-6 lg:w-6" />
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        {/* Chart */}
        <AreaStatsChart
          data={chartData}
          currency="Rs"
          title={t("volumeOverviewTitle")}
        />

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl p-6 border border-slate-100"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">
              {t("recentTransactions")}
            </h3>
            <Link to={createPageUrl("ResellerTransactions")}>
              <Button
                variant="ghost"
                className="text-violet-600 hover:text-violet-700"
              >
                {t("viewAll")}
              </Button>
            </Link>
          </div>

          <TransactionTable
            transactions={recentTransactions}
            isLoading={isRecentLoading}
            onRowClick={setSelectedTx}
          />
        </motion.div> 

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-2xl p-4 lg:p-6 shadow-sm border border-slate-100"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4">{t("quickActions")}</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action) => (
              <Link key={action.to} to={createPageUrl(action.to)}>
                <Button variant="outline" className="w-full h-auto py-4 flex-col gap-2">
                  <action.icon className={`h-5 w-5 ${action.iconColor}`} />
                  <span className="text-sm">{action.label}</span>
                </Button>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>

      <TransactionDetailPanel
        transaction={selectedTx}
        open={!!selectedTx}
        onClose={() => setSelectedTx(null)}
        defaultCurrency={selectedTx?.currency || "PKR"}
      />
    </ResellerLayout>
  );
}